import { useCallback, useEffect, useState } from "react"

export type Appearance = "light" | "dark" | "system"

const isBrowser = () => typeof window !== "undefined"

const prefersDark = () => {
  if (!isBrowser()) return false
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

const applyTheme = (appearance: Appearance) => {
  if (!isBrowser()) return

  const isDark =
    appearance === "dark" || (appearance === "system" && prefersDark())

  document.documentElement.classList.toggle("dark", isDark)
  document.documentElement.style.colorScheme = isDark ? "dark" : "light"
}

const mediaQuery = () => {
  if (!isBrowser()) return null

  return window.matchMedia("(prefers-color-scheme: dark)")
}

const readAppearance = (): Appearance | null => {
  if (!isBrowser()) return null
  return localStorage.getItem("appearance") as Appearance | null
}

const handleSystemThemeChange = () => {
  applyTheme(readAppearance() ?? "system")
}

export function initializeTheme() {
  if (!isBrowser()) return

  applyTheme(readAppearance() ?? "system")
  mediaQuery()?.addEventListener("change", handleSystemThemeChange)
}

export function useAppearance() {
  const [appearance, setAppearance] = useState<Appearance>(
    () => readAppearance() ?? "system",
  )

  const updateAppearance = useCallback((mode: Appearance) => {
    setAppearance(mode)
    if (!isBrowser()) return

    if (mode === "system") {
      localStorage.removeItem("appearance")
    } else {
      localStorage.setItem("appearance", mode)
    }
    applyTheme(mode)
  }, [])

  useEffect(() => {
    applyTheme(appearance)

    return () =>
      mediaQuery()?.removeEventListener("change", handleSystemThemeChange)
  }, [appearance])

  return { appearance, updateAppearance } as const
}
